import React, { SetStateAction, useEffect, useState } from 'react'
import { Link, Route } from 'react-router-dom'
import { AuthRoute, FlexRoutes } from 'src/routes'
import { Box, Button, Tab, TabList, Tabs } from 'src/ui'
import { FlavorDialog, FlavorWrapper } from 'src/companies/branding/flavor-create-helper'
import { BuildConfigPage } from 'src/companies/build-manager/build-config'
import { Dispatch } from 'src/store'
import { CreateAcadiaBuildForm } from './create-acadia-build'

interface BuildManagerPageProps {
  setSettingsState: Dispatch['settings']['setState']
  slug: string
  company: Company
  companies: Company[]
}

export default function BuildManagerPage({
  slug,
  company,
  companies,
  setSettingsState,
  ...rest
}: BuildManagerPageProps) {
  useEffect(() => {
    setSettingsState({ showCompanySelectorDialog: true, showCentreEnvironment: false })
  }, [setSettingsState])

  return (
    <FlavorWrapper
      companies={companies}
      slug={slug}
      component={BuildManagerRoutes}
      componentProps={{ company, ...rest }}
    />
  )
}
interface BuildManagerRoutesProps {
  company: Company
  flavor: Flavor
}

function BuildManagerRoutes({ company, flavor, ...rest }: Readonly<BuildManagerRoutesProps>) {
  const [isEditingFlavor, setIsEditingFlavor] = useState(false)

  return (
    <>
      <FlexRoutes>
        <Route
          path="/configuration"
          element={
            <AuthRoute
              header={
                <BuildManagerHeader
                  type="build-config"
                  company={company}
                  setIsEditingFlavor={setIsEditingFlavor}
                  {...rest}
                />
              }
              company={company}
              flavor={flavor}
              as={BuildConfigPage}
            />
          }
        />
        <Route
          path="*"
          element={
            <AuthRoute
              header={
                <BuildManagerHeader
                  type="acadia-build-create"
                  company={company}
                  setIsEditingFlavor={setIsEditingFlavor}
                  {...rest}
                />
              }
              company={company}
              flavor={flavor}
              as={CreateAcadiaBuildForm}
            />
          }
        />
      </FlexRoutes>
      <FlavorDialog
        isOpen={isEditingFlavor}
        onClose={() => setIsEditingFlavor(false)}
        onConfirm={() => setIsEditingFlavor(false)}
        flavor={flavor}
      />
    </>
  )
}
interface BuildManagerHeaderProps {
  type: 'build-config' | 'acadia-build-create'
  company: Company
  setIsEditingFlavor: React.Dispatch<SetStateAction<boolean>>
}

function BuildManagerHeader({
  type,
  company,
  setIsEditingFlavor,
}: Readonly<BuildManagerHeaderProps>) {
  return (
    <Box display="inline-flex" alignItems="center" justifyContent="space-between">
      <Tabs variant="unstyled">
        <TabList>
          <Tab
            as={Link}
            to={`/companies/${company.slug}/build-manager`}
            fontWeight={700}
            color={type === 'acadia-build-create' ? 'blue.500' : 'gray.500'}
            borderBottom="2px"
            borderBottomColor={type === 'acadia-build-create' ? 'blue.500' : 'transparent'}
          >
            Create an Acadia Build
          </Tab>
          <Tab
            as={Link}
            to={`/companies/${company.slug}/build-manager/configuration`}
            fontWeight={700}
            color={type === 'build-config' ? 'blue.500' : 'gray.500'}
            borderBottom="2px"
            borderBottomColor={type === 'build-config' ? 'blue.500' : 'transparent'}
          >
            Build Config
          </Tab>
        </TabList>
      </Tabs>
      <Button variant="ghost" size="sm" onClick={() => setIsEditingFlavor(true)}>
        Edit Flavor
      </Button>
    </Box>
  )
}
